/**
 * SchadenMeldenDialog — Schadenmeldung aus einer aktiven Pool-Reservierung (Issue #167).
 *
 * Öffnet sich über "Schaden melden"-Button auf der PoolFahrzeugCard.
 * Legt direkt eine Akte im Aktensystem an und setzt pool_reservierungen.schadenbericht.
 * Navigiert nach Erfolg zur neuen Akte.
 */

import { useState } from 'react';
import { AlertTriangle, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLocation } from 'wouter';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { generiereAktenNr, generiereAktenNummer } from '@/lib/akten';
import { buildSchadenAktePayload, buildSchadenbericht } from '@/lib/schaden-aus-reservierung';

export interface SchadenMeldenDialogProps {
  open: boolean;
  reservierungId: string;
  fahrzeugId: string;
  fahrerMitarbeiterId: string;
  kennzeichen: string;
  fahrzeugMarke: string;
  fahrzeugModell: string;
  onClose: () => void;
}

export default function SchadenMeldenDialog({
  open,
  reservierungId,
  fahrzeugId,
  fahrerMitarbeiterId,
  kennzeichen,
  fahrzeugMarke,
  fahrzeugModell,
  onClose,
}: SchadenMeldenDialogProps) {
  const [, setLocation] = useLocation();
  const [beschreibung, setBeschreibung] = useState('');
  const [ort, setOrt] = useState('');
  const [zeitpunkt, setZeitpunkt] = useState<string | null>(new Date().toISOString());
  const [gegnerKennzeichen, setGegnerKennzeichen] = useState('');
  const [fotoFiles, setFotoFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setFotoFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeFoto = (idx: number) => {
    setFotoFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSpeichern = async () => {
    if (!beschreibung.trim()) return;

    setSaving(true);
    try {
      // 1. Upload photos
      const fotoUrls: string[] = [];
      for (const file of fotoFiles) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${reservierungId}/schaden/${Date.now()}_${safeName}`;
        const { error: uploadErr } = await supabase.storage
          .from('akten-dokumente')
          .upload(path, file, { upsert: false });
        if (uploadErr) {
          toast.error(`Foto-Upload fehlgeschlagen: ${uploadErr.message}`);
          return;
        }
        const { data: urlData } = supabase.storage
          .from('akten-dokumente')
          .getPublicUrl(path);
        if (urlData.publicUrl) fotoUrls.push(urlData.publicUrl);
      }

      // 2. Build Akte payload
      const zeitpunktIso = zeitpunkt ?? new Date().toISOString();
      const basePayload = buildSchadenAktePayload({
        reservierungId,
        fahrzeugId,
        fahrerMitarbeiterId,
        kennzeichen,
        fahrzeugMarke,
        fahrzeugModell,
        beschreibung: beschreibung.trim(),
        zeitpunkt: zeitpunktIso,
        ort: ort.trim() || null,
        gegnerKennzeichen: gegnerKennzeichen.trim() || null,
        fotoUrls: fotoUrls.length ? fotoUrls : null,
      });

      // 3. Generate unique Aktenzeichen
      const aktenNr = generiereAktenNr();

      const insertPayload: Record<string, unknown> = {
        ...basePayload,
        akten_nr: aktenNr,
        akten_nummer: generiereAktenNummer('gutachten', aktenNr),
        mwst_satz: 19,
        status_sv: 'neu',
      };

      // 4. Create Akte
      const { data: akteData, error: akteErr } = await supabase
        .from('akten')
        .insert(insertPayload)
        .select('id, akten_nummer, akten_nr, business_unit')
        .single();

      if (akteErr) {
        toast.error(`Akte konnte nicht angelegt werden: ${akteErr.message}`);
        return;
      }

      const akte = akteData as { id: string; akten_nummer: string; akten_nr: string; business_unit: string };
      const finalAktenNr = akte.akten_nummer || akte.akten_nr;

      // 5. Set schadenbericht on reservation
      const bericht = buildSchadenbericht(beschreibung.trim(), finalAktenNr);
      const { error: patchErr } = await supabase
        .from('pool_reservierungen')
        .update({ schadenbericht: bericht })
        .eq('id', reservierungId);

      if (patchErr) {
        // Non-fatal: akte was created, just log
        console.error('schadenbericht setzen fehlgeschlagen:', patchErr.message);
      }

      toast.success(`Akte ${finalAktenNr} angelegt — Schaden gemeldet`);
      onClose();
      setLocation(`/akten/${akte.business_unit}/${finalAktenNr}/uebersicht`);
    } finally {
      setSaving(false);
    }
  };

  const label = `${fahrzeugMarke} ${fahrzeugModell}${kennzeichen ? ` (${kennzeichen})` : ''}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Schaden melden"
      data-testid="schaden-melden-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => !saving && onClose()}
        aria-hidden="true"
      />

      <div className="relative z-10 bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2">
          <AlertTriangle className="size-5 text-red-600" />
          <h2 className="text-base font-semibold">Schaden melden — {label}</h2>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="schaden-beschreibung">
            Schadenbeschreibung <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="schaden-beschreibung"
            placeholder="z.B. Kratzer an der Fahrertür, Delle vorne links…"
            rows={3}
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
            maxLength={1000}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="schaden-ort">Ort (optional)</Label>
            <Input
              id="schaden-ort"
              placeholder="z.B. Dortmund, Rheinlanddamm"
              value={ort}
              onChange={(e) => setOrt(e.target.value)}
              maxLength={200}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Datum / Uhrzeit</Label>
            <DateTimePicker
              value={zeitpunkt}
              onChange={setZeitpunkt}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="schaden-gegner-kz">Gegner-Kennzeichen (optional)</Label>
          <Input
            id="schaden-gegner-kz"
            placeholder="z.B. DO-AB 1234"
            value={gegnerKennzeichen}
            onChange={(e) => setGegnerKennzeichen(e.target.value.toUpperCase())}
            maxLength={20}
          />
        </div>

        {/* Photo upload */}
        <div className="space-y-2">
          <Label>Fotos (optional)</Label>
          <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors text-sm text-gray-500">
            <Upload className="size-4" />
            Fotos auswählen
            <input
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              className="sr-only"
              onChange={handleFotoChange}
            />
          </label>

          {fotoFiles.length > 0 && (
            <ul className="space-y-1">
              {fotoFiles.map((f, i) => (
                <li key={i} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                  <span className="truncate">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFoto(i)}
                    className="ml-2 text-gray-400 hover:text-red-500 shrink-0"
                    aria-label={`Foto ${f.name} entfernen`}
                  >
                    <X className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            size="sm"
            data-testid="schaden-speichern-btn"
            onClick={handleSpeichern}
            disabled={saving || !beschreibung.trim()}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {saving ? (
              <>
                <Loader2 className="size-3.5 animate-spin mr-1.5" />
                Akte anlegen…
              </>
            ) : (
              'Akte anlegen'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
